import * as React from "react";
import { Link, useLocation } from "react-router";

import {
  Tab,
  TabList,
  Tabs,
  TabSeparator,
} from "@/shared/foundation/primitives/Tabs";

import styles from "./EntityTabs.module.css";

export interface EntityTabDef<T extends string = string> {
  /** The path segment the tab's route is mounted at under the page. */
  value: T;
  /** The tab's name, as shown and as its accessible name. */
  label: string;
  /**
   * Before the label, as tall as its text: the record type's `EntityIcon` for
   * a tab that lists records, otherwise a fixed UI icon (Details `Info`, …).
   */
  icon?: React.ReactNode;
  /**
   * Where the tab links to, relative to the page, when not its `value`: the
   * dataset Files tab returns to the folder last open in it.
   */
  to?: string;
  /** After the label: a status, such as the dataset Actions tab's. */
  endAdornment?: React.ReactElement;
}

/** A tab, or a rule between groups of tabs. */
export type EntityTabItem<T extends string = string> =
  EntityTabDef<T> | "divider";

export interface EntityTabsProps<T extends string = string> {
  /**
   * In order. Leave out tabs behind a feature flag that is off before
   * passing them.
   */
  tabs: readonly EntityTabItem<T>[];
  /** From {@link useActiveEntityTab}. */
  activeTab: T;
  /**
   * Carry the current query string to the tab a link opens. Off for a page
   * whose tabs each keep their own state in it.
   * @default true
   */
  keepSearch?: boolean;
  /** Names the tab list for assistive tech, as "dataset tabs". */
  "aria-label"?: string;
}

/**
 * The tab strip under a resource page's header. Each tab links to its route,
 * keeps the current query string unless `keepSearch` is off, and replaces the
 * history entry rather than adding one. The strip is pulled one tab's padding
 * left, so the first label lines up with the header text above it.
 */
export function EntityTabs<T extends string>({
  tabs,
  activeTab,
  keepSearch = true,
  "aria-label": ariaLabel,
}: EntityTabsProps<T>) {
  const location = useLocation();

  return (
    <Tabs value={activeTab} className={styles.strip}>
      <TabList aria-label={ariaLabel} className={styles.list}>
        {tabs.map((tab, i) =>
          tab === "divider" ? (
            <TabSeparator key={`divider-${i}`} />
          ) : (
            <Tab
              key={tab.value}
              value={tab.value}
              nativeButton={false}
              render={
                <Link
                  to={{
                    pathname: tab.to ?? tab.value,
                    search: keepSearch ? location.search : "",
                  }}
                  replace
                />
              }
            >
              {tab.icon && (
                <span aria-hidden className={styles.icon}>
                  {tab.icon}
                </span>
              )}
              {tab.label}
              {tab.endAdornment}
            </Tab>
          ),
        )}
      </TabList>
    </Tabs>
  );
}
